import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { User, Phone, MapPin, Wallet, Globe, Save, ChevronRight, Sparkles, Gift } from "lucide-react";
import { INDIAN_STATES } from "../../lib/indianStates";
import { FLYER_LANGS } from "../../lib/flyerI18n";
import { Link } from "react-router-dom";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:bg-white focus:border-[#1B5E20] focus:ring-2 focus:ring-emerald-600/20 transition-all";

export default function ScProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState({ city: "", state: "", upi_id: "", preferred_language: "en" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/sc/flyer-data");
      setForm({
        city: data.sc?.city || "",
        state: data.sc?.state || "",
        upi_id: "",
        preferred_language: user?.preferred_language || "en",
      });
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = {};
      if (form.city) payload.city = form.city;
      if (form.state) payload.state = form.state;
      if (form.upi_id) payload.upi_id = form.upi_id;
      if (form.preferred_language) payload.preferred_language = form.preferred_language;
      await api.put("/sc/profile", payload);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) return <div className="font-mono text-xs text-slate-400 mt-10 text-center">Loading…</div>;

  return (
    <div data-testid="sc-profile" className="pb-6">
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Your account</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1 text-slate-900">Profile.</h1>

      {/* Identity card */}
      <div className="mt-5 bg-gradient-to-br from-[#1B5E20] to-[#134A18] p-5 rounded-2xl shadow-[0_4px_20px_rgba(27,94,32,0.25)] flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <User className="w-6 h-6 text-white" />
        </div>
        <div className="min-w-0">
          <div className="text-lg font-bold tracking-tight text-white truncate">{user?.name}</div>
          <div className="font-mono text-[11px] text-emerald-100/80 flex items-center gap-1 mt-0.5">
            <Phone className="w-3 h-3" /> {user?.phone || "—"}
          </div>
        </div>
      </div>

      {/* Editable fields */}
      <div className="mt-5 space-y-3.5">
        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1.5">
            <Globe className="w-3 h-3" /> Preferred language (Toolkit)
          </label>
          <div className="flex flex-wrap gap-1.5" data-testid="sc-profile-lang-row">
            {FLYER_LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => setField("preferred_language", l.code)}
                data-testid={`sc-profile-lang-${l.code}`}
                className={"px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " +
                  (form.preferred_language === l.code
                    ? "bg-[#1B5E20] text-white border-[#1B5E20]"
                    : "border-slate-200 text-slate-600 bg-white")}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1.5">
            <MapPin className="w-3 h-3" /> City
          </label>
          <input
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
            placeholder="Your city"
            data-testid="sc-profile-city"
            className={inputCls}
          />
        </div>

        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1.5">
            <MapPin className="w-3 h-3" /> State
          </label>
          <select
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
            data-testid="sc-profile-state"
            className={inputCls}
          >
            <option value="">Select state…</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div>
          <label className="font-mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1.5">
            <Wallet className="w-3 h-3" /> UPI ID (for payouts)
          </label>
          <input
            value={form.upi_id}
            onChange={(e) => setField("upi_id", e.target.value)}
            placeholder="yourname@upi"
            data-testid="sc-profile-upi"
            className={inputCls + " font-mono"}
          />
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="sc-profile-save"
          className="w-full bg-[#1B5E20] hover:bg-[#15501A] text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm active:scale-[0.99] transition-all"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save profile"}
        </button>
      </div>

      {/* Quick links */}
      <div className="mt-6 space-y-2">
        <Link to="/sc/marketing" className="border border-slate-200 bg-white p-3 rounded-xl flex items-center justify-between hover:border-emerald-300 shadow-[0_2px_8px_rgba(15,23,42,0.04)]" data-testid="sc-profile-link-marketing">
          <div className="text-sm font-semibold flex items-center gap-1.5 text-slate-900"><Sparkles className="w-3.5 h-3.5 text-[#D97706]" /> Toolkit</div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </Link>
        <Link to="/sc/marketing/schemes" className="border border-slate-200 bg-white p-3 rounded-xl flex items-center justify-between hover:border-emerald-300 shadow-[0_2px_8px_rgba(15,23,42,0.04)]" data-testid="sc-profile-link-schemes">
          <div className="text-sm font-semibold flex items-center gap-1.5 text-slate-900"><Gift className="w-3.5 h-3.5 text-[#1B5E20]" /> Incentive schemes</div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </Link>
      </div>
    </div>
  );
}
